import React, { useState, useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { MoreHorizontal, Upload, UserPlus, Users, UserCheck, UserX, CheckCircle, XCircle, ArrowUpDown } from 'lucide-react';
import { getUsers, approveUser, suspendUser, activateUser, updateUserRole, deleteUser, User, UserApproveRequest } from '../../services/userService';

const UserManagementPage: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [approvingUser, setApprovingUser] = useState<string | null>(null);
  const [selectedRole, setSelectedRole] = useState<UserApproveRequest['role']>('contributor');
  const [activeTab, setActiveTab] = useState<'all' | 'approved' | 'pending' | 'suspended' | 'demo_requests'>('all');
  const [sortField, setSortField] = useState<'name' | 'created_at'>('created_at');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [demoRequestFilter, setDemoRequestFilter] = useState<boolean | null>(null);

  useEffect(() => {
    loadUsers();
  }, [demoRequestFilter]);

  const loadUsers = async () => {
    try {
      const userData = await getUsers(demoRequestFilter);
      setUsers(userData);
    } catch (error) {
      console.error('Failed to load users:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApproveUser = async (userId: string) => {
    setApprovingUser(userId);
    try {
      await approveUser(userId, { role: selectedRole });
      await loadUsers(); // Reload users to reflect changes
    } catch (error) {
      console.error('Failed to approve user:', error);
    } finally {
      setApprovingUser(null);
    }
  };

  const handleSuspendUser = async (userId: string) => {
    try {
      await suspendUser(userId);
      await loadUsers(); // Reload users to reflect changes
    } catch (error) {
      console.error('Failed to suspend user:', error);
    }
  };

  const handleActivateUser = async (userId: string) => {
    try {
      await activateUser(userId);
      await loadUsers(); // Reload users to reflect changes
    } catch (error) {
      console.error('Failed to activate user:', error);
    }
  };

  const handleDeleteUser = async (userId: string) => {
    if (!window.confirm('Are you sure you want to delete this user? This action cannot be undone.')) {
      return;
    }

    try {
      await deleteUser(userId);
      await loadUsers(); // Reload users to reflect changes
    } catch (error) {
      console.error('Failed to delete user:', error);
    }
  };

  // Filter users based on active tab
  const getFilteredUsers = () => {
    let filteredUsers = [...users];

    switch (activeTab) {
      case 'approved':
        filteredUsers = filteredUsers.filter(user => user.status === 'active');
        break;
      case 'pending':
        filteredUsers = filteredUsers.filter(user => user.status === 'pending_approval');
        break;
      case 'suspended':
        filteredUsers = filteredUsers.filter(user => user.status === 'suspended');
        break;
      case 'demo_requests':
        filteredUsers = filteredUsers.filter(user => user.demo_request === true);
        break;
      case 'all':
      default:
        // Show all users
        break;
    }

    return filteredUsers;
  };

  // Sort users
  const getSortedUsers = () => {
    const filteredUsers = getFilteredUsers();

    return filteredUsers.sort((a, b) => {
      let aValue, bValue;

      if (sortField === 'name') {
        aValue = a.name.toLowerCase();
        bValue = b.name.toLowerCase();
      } else {
        aValue = new Date(a.created_at).getTime();
        bValue = new Date(b.created_at).getTime();
      }

      if (sortDirection === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });
  };

  const handleSort = (field: 'name' | 'created_at') => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };
  const [newUser, setNewUser] = useState({
    fname: '',
    familyName: '',
    email: '',
    role: 'user'
  });

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return <Badge variant="default" className="bg-green-500">Active</Badge>;
      case 'pending_approval':
        return <Badge variant="secondary" className="bg-yellow-500">Pending</Badge>;
      case 'suspended':
        return <Badge variant="destructive">Suspended</Badge>;
      default:
        return <Badge variant="outline">Unknown</Badge>;
    }
  };

  const getRoleBadge = (role: string) => {
    switch (role?.toLowerCase()) {
      case 'super_admin':
        return <Badge variant="destructive">Super Admin</Badge>;
      case 'admin':
        return <Badge variant="destructive">Admin</Badge>;
      case 'researcher':
        return <Badge variant="default" className="bg-blue-500">Researcher</Badge>;
      case 'contributor':
        return <Badge variant="outline">Contributor</Badge>;
      default:
        return <Badge variant="outline">Unknown</Badge>;
    }
  };

  const handleCSVUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    // CSV upload functionality disabled for now
    alert('CSV upload functionality is temporarily disabled');
    event.target.value = '';
  };

  const handleAddUser = () => {
    // Manual user addition disabled for now
    alert('Manual user addition is temporarily disabled. Users should register through the registration form.');
  };

  const activeUsers = users.filter(user => user.status === 'active');
  const pendingUsers = users.filter(user => user.status === 'pending_approval');
  const suspendedUsers = users.filter(user => user.status === 'suspended');
  const adminUsers = users.filter(user => ['admin', 'super_admin'].includes(user.role?.toLowerCase() || ''));
  const demoRequests = users.filter(user => user.demo_request === true);

  return (
    <div className="space-y-6">
      <h1 className="text-3xl font-bold">User Management</h1>

      {/* User Dashboard */}
      <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Users</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{users.length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Active Users</CardTitle>
            <UserCheck className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{activeUsers.length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Pending Approval</CardTitle>
            <UserPlus className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{pendingUsers.length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Suspended Users</CardTitle>
            <UserX className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{suspendedUsers.length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Admin Users</CardTitle>
            <UserPlus className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{adminUsers.length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Demo Requests</CardTitle>
            <UserPlus className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{demoRequests.length}</div>
          </CardContent>
        </Card>
      </div>

      {/* User Addition Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* CSV Upload */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Upload className="h-5 w-5" />
              Batch User Registration
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <p className="text-sm text-muted-foreground">
                Upload a CSV file with the following format:
              </p>
              <div className="bg-muted p-3 rounded text-xs font-mono">
                fname,family name,email,role<br />
                John,Doe,john@example.com,user<br />
                Jane,Smith,jane@example.com,admin<br />
                Bob,Johnson,bob@example.com,validator
              </div>
            </div>
            <div>
              <Input
                type="file"
                accept=".csv"
                onChange={handleCSVUpload}
                className="cursor-pointer"
              />
            </div>
          </CardContent>
        </Card>

        {/* Manual User Addition */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <UserPlus className="h-5 w-5" />
              Add User Manually
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <Input
                placeholder="First Name"
                value={newUser.fname}
                onChange={(e) => setNewUser(prev => ({ ...prev, fname: e.target.value }))}
              />
              <Input
                placeholder="Family Name"
                value={newUser.familyName}
                onChange={(e) => setNewUser(prev => ({ ...prev, familyName: e.target.value }))}
              />
            </div>
            <Input
              placeholder="Email"
              type="email"
              value={newUser.email}
              onChange={(e) => setNewUser(prev => ({ ...prev, email: e.target.value }))}
            />
            <select
              className="w-full p-2 border rounded"
              value={newUser.role}
              onChange={(e) => setNewUser(prev => ({ ...prev, role: e.target.value }))}
            >
              <option value="user">User</option>
              <option value="validator">Validator</option>
              <option value="admin">Admin</option>
            </select>
            <Button onClick={handleAddUser} className="w-full">
              Add User
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Tab Navigation */}
      <div className="border-b">
        <nav className="-mb-px flex space-x-8">
          {[
            { key: 'all', label: 'All Users', count: users.length },
            { key: 'approved', label: 'Approved', count: activeUsers.length },
            { key: 'pending', label: 'Pending', count: pendingUsers.length },
            { key: 'suspended', label: 'Suspended', count: suspendedUsers.length },
            { key: 'demo_requests', label: 'Demo Requests', count: demoRequests.length }
          ].map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key as any)}
              className={`py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${activeTab === tab.key
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
            >
              {tab.label}
              <span className="ml-2 py-0.5 px-2 text-xs bg-gray-200 rounded-full">
                {tab.count}
              </span>
            </button>
          ))}
        </nav>
      </div>

      {/* Users Table */}
      <div className="border rounded-lg">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead
                className="cursor-pointer hover:bg-gray-50"
                onClick={() => handleSort('name')}
              >
                <div className="flex items-center">
                  Name
                  <ArrowUpDown className="ml-2 h-4 w-4" />
                </div>
              </TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Role</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Demo Request</TableHead>
              <TableHead>Country</TableHead>
              <TableHead>Sector</TableHead>
              <TableHead
                className="cursor-pointer hover:bg-gray-50"
                onClick={() => handleSort('created_at')}
              >
                <div className="flex items-center">
                  Created
                  <ArrowUpDown className="ml-2 h-4 w-4" />
                </div>
              </TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-8">
                  Loading users...
                </TableCell>
              </TableRow>
            ) : getSortedUsers().length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-8">
                  No {activeTab === 'all' ? '' : activeTab} users found
                </TableCell>
              </TableRow>
            ) : (
              getSortedUsers().map((user) => (
                <TableRow key={user.id}>
                  <TableCell className="font-medium">{user.name}</TableCell>
                  <TableCell className="font-medium">{user.email}</TableCell>
                  <TableCell>{getRoleBadge(user.role)}</TableCell>
                  <TableCell>{getStatusBadge(user.status)}</TableCell>
                  <TableCell>
                    {user.demo_request ? (
                      <Badge variant="default" className="bg-blue-500">Yes</Badge>
                    ) : (
                      <Badge variant="outline">No</Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-sm">
                    {user.country || '-'}
                  </TableCell>
                  <TableCell className="text-sm">
                    {user.sector || '-'}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {new Date(user.created_at).toLocaleString()}
                  </TableCell>
                  <TableCell className="text-right">
                    {user.status === 'pending_approval' ? (
                      <div className="flex justify-end gap-2">
                        <select
                          value={selectedRole}
                          onChange={(e) => setSelectedRole(e.target.value as UserApproveRequest['role'])}
                          className="text-sm border rounded px-2 py-1"
                        >
                          <option value="contributor">Contributor</option>
                          <option value="researcher">Researcher</option>
                          <option value="admin">Admin</option>
                          <option value="super_admin">Super Admin</option>
                        </select>
                        <Button
                          size="sm"
                          onClick={() => handleApproveUser(user.id)}
                          disabled={approvingUser === user.id}
                          className="flex items-center gap-1"
                        >
                          {approvingUser === user.id ? (
                            'Approving...'
                          ) : (
                            <>
                              <CheckCircle className="h-4 w-4" />
                              Approve
                            </>
                          )}
                        </Button>
                      </div>
                    ) : (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" className="h-8 w-8 p-0">
                            <span className="sr-only">Open menu</span>
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          {user.status === 'active' ? (
                            <DropdownMenuItem onClick={() => handleSuspendUser(user.id)}>
                              <XCircle className="h-4 w-4 mr-2" />
                              Suspend User
                            </DropdownMenuItem>
                          ) : (
                            <DropdownMenuItem onClick={() => handleActivateUser(user.id)}>
                              <CheckCircle className="h-4 w-4 mr-2" />
                              Activate User
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            className="text-red-500"
                            onClick={() => handleDeleteUser(user.id)}
                          >
                            Delete User
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

export default UserManagementPage;